// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { Component } from './component';
import { Cookie } from './cookie';
import { DropdownManager } from './dropdown-manager';
import { render, createElement as ce } from './common';

/**
 * @class Jsw.ActiveList
 * @extends Jsw.Component
 *
 * Example usage:
 *
 *     @example
 *     new Jsw.ActiveList({
 *         renderTo: document.body,
 *         toolbar: {
 *             buttons: {
 *                 btn1: {
 *                     title: 'Add new item',
 *                     url: '#!'
 *                 }
 *             }
 *         },
 *         layout: 'responsivecolumn',
 *         data: [{
 *             title: 'Item 1',
 *             icon: 'class-icons/component-large.png',
 *             buttons: [{
 *                 title: 'Action 1',
 *                 icon: 'class-icons/class-large.png'
 *             }, {
 *                 title: 'Action 2',
 *                 icon: 'class-icons/singleton-large.png'
 *             }]
 *         }, {
 *             title: 'Item 2',
 *             icon: 'class-icons/component-large.png',
 *             buttons: [{
 *                 title: 'Action 1',
 *                 icon: 'class-icons/class-large.png'
 *             }, {
 *                 title: 'Action 2',
 *                 icon: 'class-icons/singleton-large.png'
 *             }]
 *         }]
 *     });
 */
export const ActiveList = Class.create(Component, {
    _initConfiguration($super, config) {
        $super(config);

        this.icons = this._getConfigParam('icons', {});
        this.urls = this._getConfigParam('urls', {});

        this.isCollapsible = this._getConfigParam('isCollapsible', false);
        this.isNew = this._getConfigParam('isNew', false);
        this.isShowItemTitle = this._getConfigParam('isShowItemTitle', true);
        this.layout = this._getConfigParam('layout', 'auto');
        if (Object.isString(this.layout)) {
            this.layout = {
                type: this.layout,
            };
        }

        this.stateCollapsed = (function (cookieName) {
            let stateCollapsed = Cookie.get(cookieName);

            try {
                stateCollapsed = stateCollapsed.evalJSON();
            } catch (e) {
                stateCollapsed = {};
            }

            return {
                get(item) {
                    return stateCollapsed[item.id] !== undefined;
                },
                set(item, value) {
                    if (value) {
                        stateCollapsed[item.id] = 1;
                    } else {
                        delete stateCollapsed[item.id];
                    }
                    Cookie.setPermanent(cookieName, Object.toJSON(stateCollapsed), '/');
                },
            };
        }(`${this.getId()}-state-collapsed`));

        this.itemActions = this._getConfigParam('itemActions', {});
        this.data = this._getConfigParam('data', []);

        this._cls = `active-list${this.isCollapsible ? ' active-list-collapsible' : ''}`;
    },

    _initComponentElement($super) {
        $super();

        render(this._componentElement, this.view());
    },

    view() {
        return ce('.active-list-wrap',
            this.toolbarView(this._getConfigParam('toolbar')),
            this.itemsView(),
            this.bottomBarView(this._getConfigParam('countMoreItems', 0))
        );
    },

    toolbarView(toolbar) {
        if (!toolbar) {
            return '';
        }

        return ce('.a-toolbar.a-toolbar-collapsed',
            ce('.a-toolbar-wrap',
                ce('.a-toolbar-buttons',
                    ce('.a-toolbar-buttons-wrap',
                        ce('table.a-toolbar-buttons-table[cellspacing=0]',
                            ce('tbody',
                                ce('tr',
                                    ce('td.a-toolbar-buttons-table-main',
                                        ce('.a-toolbar-buttons-main',
                                            Object.keys(toolbar.buttons).map(function (id) {
                                                return ce('a.btn', {
                                                    id,
                                                    href: toolbar.buttons[id].url.escapeHTML(),
                                                }, ` ${toolbar.buttons[id].title}`.escapeHTML());
                                            })
                                        )
                                    ),
                                    ce('td.a-toolbar-buttons-table-misc',
                                        ce('.a-toolbar-buttons-misc',
                                            toolbar.showInformer ? ce('span.btn.btn-informer', { title: this.lmsg('informerTitle') },
                                                ce('button[type=button]',
                                                    {
                                                        onclick: this.showInformer.bind(this),
                                                    },
                                                    ce('i.icon', ce('img[alt=""]', { src: this.icons['informer-toolbar'] }))
                                                )
                                            ) : '',
                                            toolbar.showSearch ? ce('span.btn.btn-search', { title: this.lmsg('searchTitle') },
                                                ce('button[type=button]', ce('i.icon', ce('img[alt=""]', { src: this.icons['search-toolbar'] })))
                                            ) : '',
                                            ce('.a-toolbar-buttons-settings.a-toolbar-buttons-settings-inactive',
                                                ce('span.btn.btn-settings', { title: this.lmsg('settingsTitle') },
                                                    ce('button[type=button]', ce('i.icon', ce('img[alt=""]', { src: this.icons['settings-toolbar'] })))
                                                ),
                                                this.isNew ? ce('span.badge-new', this.lmsg('badgeNew')) : '',
                                                ce('.popup-box.a-toolbar-buttons-settings-popup',
                                                    ce('table.popup-wrapper[cellspacing=0]', ce('tbody', ce('tr', ce('td.popup-container',
                                                        ce('.c1', ce('.c2', ce('.c3', ce('.c4', ce('.c5',
                                                            ce('.popup-content',
                                                                ce('.popup-content-area',
                                                                    ce('.settings-list',
                                                                        ce('.settings-item.settings-item-1',
                                                                            ce('h3.settings-item-title',
                                                                                ce('span', `${this.lmsg('sortTitle')}:`)
                                                                            ),
                                                                            ce(`a.btn${toolbar.sortDir === 'up' ? '.btn-state-selected' : ''}`,
                                                                                { href: this.urls['sort-up'] },
                                                                                ce('i.icon', ce('img[alt=""]', { src: this.icons['order-ascending'] })),
                                                                                this.lmsg('orderAscending')
                                                                            ),
                                                                            ce(`a.btn${toolbar.sortDir === 'down' ? '.btn-state-selected' : ''}`,
                                                                                { href: this.urls['sort-down'] },
                                                                                ce('i.icon', ce('img[alt=""]', { src: this.icons['order-descending'] })),
                                                                                this.lmsg('orderDescending')
                                                                            )
                                                                        ),
                                                                        toolbar.hasClassicView ? ce('.settings-item.settings-item-2',
                                                                            ce('h3.settings-item-title',
                                                                                ce('span', `${this.lmsg('modeTitle')}:`)
                                                                            ),
                                                                            ce('a.btn.btn-state-selected', { href: '#' },
                                                                                ce('i.icon', ce('img[alt=""]', { src: this.icons['list-type-active'] })),
                                                                                this.lmsg('typeActive')
                                                                            ),
                                                                            ce('a.btn', { href: '?list-type=classic' },
                                                                                ce('i.icon', ce('img[alt=""]', { src: this.icons['list-type-classic'] })),
                                                                                this.lmsg('typeClassic')
                                                                            )
                                                                        ) : ''
                                                                    )
                                                                )
                                                            )
                                                        )))))
                                                    ))))
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                )
            )
        );
    },

    itemsView() {
        if (!this.data.length) {
            return this.emptyView();
        }

        const items = this.data.map(this.itemView.bind(this));
        if (this.layout.type !== 'responsivecolumn') {
            return items;
        }

        let cls = ['.grid'];
        if (this.layout.stretched !== false) {
            cls.push('.grid-stretched');
        }
        cls = cls.concat((this.layout.columns || 'xl-2 xxl-3').split(' ').map(cls => `.grid-${cls}`));

        return ce(cls.join(''),
            items.map(function (item) {
                return ce('.grid-col', item);
            })
        );
    },

    emptyView() {
        return ce('p', this.lmsg('noObjects'));
    },

    itemView(item) {
        const itemClasses = [
            '.active-list-item',
        ];
        const types = [
            'danger',
            'warning',
            'inactive',
            'success',
        ];

        if (item.type && types.indexOf(item.type) > -1) {
            itemClasses.push(`.active-list-item-${item.type}`);
        }
        if (this.isCollapsible && item.buttons) {
            itemClasses.push('.active-list-item-collapsible');
            itemClasses.push(this.stateCollapsed.get(item) ? '.active-list-item-collapsed' : '.active-list-item-expanded');
        } else {
            itemClasses.push('.active-list-item-expanded');
        }

        return ce(`#active-list-item-${item.id}${itemClasses.join('')}`,
            ce('.active-list-item-wrap',
                this.itemCaptionView(item),
                this.itemDetailsView(item)
            )
        );
    },

    itemDetailsView(item) {
        const children = [];
        if (item.settings && item.settings.length) {
            children.push(this.itemSettingsView(item));
        }
        if (item.buttons && item.buttons.length) {
            children.push(this.itemToolsView(item));
        }

        if (!children.length) {
            return '';
        }

        return ce('.active-list-details',
            ce('.active-list-details-wrap',
                children
            )
        );
    },

    itemSettingsView(item) {
        return ce('.list',
            ce(`table#active-list-item-settings-table-${item.id}[width=100%][cellspacing=0]`,
                ce('tbody',
                    item.settings.map(function (settings, index) {
                        return ce(`tr${index % 2 ? '.even' : '.odd'}`,
                            ce('td.min',
                                ce('span.tooltipData', settings.hint.escapeHTML()),
                                ce(`a.s-btn.${settings.button}[data-method=post]`, { href: settings.url }, ce('span'))
                            ),
                            ce('td', settings.name.escapeHTML()),
                            ce('td', settings.description.escapeHTML())
                        );
                    })
                )
            )
        );
    },

    itemToolsView(item) {
        return ce('.tools-list-box',
            ce('.box-area',
                ce('.content',
                    ce('.content-area',
                        ce('ul.tools-list',
                            item.buttons.map(function (button) {
                                let onClick = null;
                                if (button.name && this.itemActions[button.name]) {
                                    onClick = function (event) {
                                        event.preventDefault();
                                        this.itemActions[button.name](item, event);
                                    }.bind(this);
                                } else if (button.handler) {
                                    onClick = Object.isFunction(button.handler)
                                        ? button.handler
                                        : `(${button.handler}(event)); return false;`;
                                }

                                return ce('li.tools-item',
                                    ce(`a.tool-block${button.disabled ? '.disabled' : ''}`,
                                        {
                                            href: !button.disabled && button.href ? button.href : null,
                                            onclick: button.disabled ? null : onClick,
                                            target: button.target || null,
                                            'data-identity': button.id || null,
                                            'data-action-name': button.name || null,
                                            rel: button.target === '_blank' ? 'noopener noreferrer' : null,
                                        },
                                        ce('span.tool-icon', ce('img[alt=""]', { src: button.icon.escapeHTML() })),
                                        ce('span.tool-name', button.title.escapeHTML()),
                                        button.new ? ce('span.badge-new', this.lmsg('badgeNew')) : '',
                                        Object.isArray(button.additionalComments) ? ce('.span.tool-info',
                                            button.additionalComments.map(function (comment) {
                                                return button.noEscape ? comment : comment.escapeHTML();
                                            }).join('<br>')
                                        ) : ''
                                    ),
                                    button.comment ? ce('span.tooltipData', button.comment.escapeHTML()) : ''
                                );
                            }.bind(this))
                        )
                    )
                )
            )
        );
    },

    bottomBarView(countMoreItems) {
        if (!countMoreItems) {
            return '';
        }

        return ce('.active-list-bottom-bar',
            ce('.active-list-bottom-bar-wrap', this.lmsg('showAll', {
                countMoreItems,
                loadAllLink: `<a href="?all=">${this.lmsg('loadAll')}</a>`,
                switchToLink: `<a href="?list-type=classic">${this.lmsg('switchTo', { listType: this.lmsg('typeClassic') })}</a>`,
            }))
        );
    },

    itemCaptionView(item) {
        return ce('.caption',
            ce('.caption-wrap',
                this.itemCaptionHeaderView(item),
                this.itemCaptionServicesView(item),
                this.itemCaptionDataView(item),
                item.additionalHtml ? ce('.caption-data',
                    ce('.caption-data-wrap', item.additionalHtml)
                ) : '',
                Object.isArray(item.buttons) && item.buttons.length ? (
                    this.isCollapsible ? ce('.caption-control',
                        {
                            onclick: this.toggleItem.bind(this, item),
                        },
                        ce('span.caption-control-wrap',
                            ce('i'), ' ',
                            ce('span.caption-control-on', this.lmsg('showMore')),
                            ce('span.caption-control-off', this.lmsg('showLess'))
                        )
                    ) : ''
                ) : ''
            )
        );
    },

    itemCaptionHeaderView(item) {
        return ce('.caption-header',
            ce('.caption-widget'),
            ce('.caption-header-wrap',
                ce('.caption-main',
                    item.icon ? ce('.caption-icon',
                        ce('span', ce('img[alt=""]', { src: item.icon }))
                    ) : '',
                    this.itemCaptionHeadView(item)
                )
            )
        );
    },

    itemCaptionHeadView(item) {
        return ce('.caption-head',
            this.isShowItemTitle ? this.itemCaptionHeadWrapView(item) : '',
            this.itemCaptionSummaryView(item),
            this.itemCaptionToolbarView(item)
        );
    },

    itemCaptionHeadWrapView(item) {
        let labels = [];
        const labelTypes = ['danger', 'warning', 'success', 'info', 'inactive'];

        if (Object.isArray(item.labels) && item.labels.length) {
            labels = item.labels.map(function (label) {
                const type = label.type && labelTypes.indexOf(label.type) > -1 ? label.type : '';
                const cls = type === '' ? '' : (`.label-${type}`);

                return ce(`span.label${cls}`, label.value || '');
            });
        }

        return ce('.caption-head-wrap',
            {
                onclick: this.isCollapsible && item.buttons ? this.onCaptionHeadWrapClick.bind(this, item) : null,
            },
            ce('h2.caption-name',
                ce('span', item.title.escapeHTML())
            ),
            labels,
            Object.isArray(item.primaryActions) && item.primaryActions.length ? ce('span.caption-head-widget',
                item.primaryActions.map(function (action) {
                    return [ce('a.btn', { href: action.link },
                        action.title
                    ), ' '];
                })
            ) : ''
        );
    },

    itemCaptionSummaryView(item) {
        return Object.isArray(item.summary) && item.summary.length ? ce('.caption-summary',
            ce('.caption-summary-wrap',
                item.summary.map(this.summaryItemView.bind(this))
            )
        ) : item.summary || '';
    },

    summaryItemView(summaryItem) {
        return ce('span.summary-item', `${summaryItem.name}: <b>${summaryItem.value}</b>`);
    },

    itemCaptionToolbarView(item) {
        return Object.isArray(item.toolbar) && item.toolbar.length ? ce('.caption-toolbar',
            ce('.caption-toolbar-wrap',
                item.toolbar.map(this.toolbarButtonView.bind(this))
            )
        ) : '';
    },

    toolbarButtonView(button) {
        return ce(`a.i-link${button.action ? `[data-item-action="${button.action}"]` : ''}`, { href: button.link || null },
            button.icon || button.iconClass ? [ce(`i${button.iconClass ? `.${button.iconClass}` : 'icon'}`,
                button.icon ? ce('img[alt=""]', { src: button.icon }) : ''
            ), ' '] : '',
            ce('span', button.title.escapeHTML())
        );
    },

    itemCaptionServicesView(item) {
        return Object.isArray(item.services) ? item.services.map(this.itemServiceView.bind(this)) : '';
    },

    itemServiceView(service) {
        return ce(`.caption-services${service.class ? `.${service.class}` : ''}`, { style: service.hidden ? 'display:none' : '' },
            ce('.caption-services-wrap',
                service.header ? ce(service.headerClass ? `.${service.headerClass}` : '',
                    service.closeUrl ? ce('span.close', { 'data-close-url': service.closeUrl }) : '',
                    service.header
                ) : '',
                this.itemServiceBlockView(service)
            )
        );
    },

    itemServiceBlockView(service) {
        if (service.grid) {
            const columns = service.grid.reduce(function (columns, service) {
                return columns + (service.colspan || 1);
            }, 0);
            return ce(`.b-grid${service.grid.length > 1 ? `.b-grid-${columns}` : ''}`,
                ce('ul.b-grid-list',
                    service.grid.map(function (subService) {
                        return ce(`li.b-grid-item${service.grid.length > 1 && subService.colspan ? `.b-grid-item-${subService.colspan}` : ''}`,
                            ce('.b-grid-item-wrap', this.itemServiceBlockView(subService))
                        );
                    }.bind(this))
                )
            );
        }

        if (service.type) {
            return this[`itemService${service.type.capitalize().camelize()}View`](service);
        }

        let icon = ce('img[alt=""]', { src: service.icon });
        let name = service.title.escapeHTML();
        if (service.href) {
            icon = ce(`a${service.newWindow ? '[target=_blank]' : ''}`, { href: service.href }, icon);
            name = ce(`a${service.newWindow ? '[target=_blank]' : ''}`, { href: service.href }, name);
        }

        const description = service.description ? service.description : '';
        return ce('.caption-service-block',
            ce('span.caption-service-title',
                ce('i.caption-service-icon', icon),
                ce('span.caption-service-name', name, description)
            ),
            Object.isArray(service.links) && service.links.length ? ce('.caption-service-toolbar',
                service.links.map(function (link) {
                    let linkView;
                    if (link.childLinks && link.childLinks.lenght) {
                        linkView = ce('.btn-group',
                            ce('a.btn', {
                                href: link.href.escapeHTML(),
                                target: link.newWindow ? '_blank' : null,
                            }, link.title.escapeHTML),
                            ce('span.btn.dropdown-toggle', ce('button[type=button]', ce('em.caret'))),
                            ce('ul.dropdown-menu',
                                link.childLinks.map(function (childLink) {
                                    return ce('li',
                                        ce('a', {
                                            href: childLink.href.escapeHTML(),
                                            target: childLink.newWindow ? '_blank' : null,
                                        }, childLink.title.escapeHTML())
                                    );
                                })
                            )
                        );
                    } else {
                        const icon = link.icon ? [ce('i.icon', ce('img[alt=""]', { src: link.icon.escapeHTML() })), ' '] : '';
                        if (link.isSimpleText) {
                            linkView = ce('span',
                                icon,
                                link.noEscape ? link.info : link.info.escapeHTML()
                            );
                        } else if (link.isSimpleLink) {
                            linkView = ce(`span${link.spanClass ? `.${link.spanClass}` : ''}`,
                                icon,
                                `${link.info.escapeHTML()} `,
                                ce('a', {
                                    href: link.href.escapeHTML(),
                                    class: link.class ? link.class.escapeHTML() : null,
                                    onclick: link.onClick ? link.onClick.escapeHTML() : null,
                                    target: link.newWindow ? '_blank' : null,
                                }, link.title.escapeHTML())
                            );
                        } else {
                            linkView = ce('a.btn', {
                                href: link.href.escapeHTML(),
                                class: link.class ? link.class.escapeHTML() : null,
                                onclick: link.onClick ? link.onClick.escapeHTML() : null,
                                target: link.newWindow ? '_blank' : null,
                                id: link.id ? link.id.escapeHTML() : null,
                                name: link.name ? link.name.escapeHTML() : null,
                            }, icon, link.title.escapeHTML());
                        }
                    }

                    return [
                        link.newLine ? ce('br') : '',
                        linkView,
                        ' ',
                    ];
                })
            ) : ''
        );
    },

    itemCaptionDataView(item) {
        if (!item.data) {
            return '';
        }

        return ce('.caption-data',
            ce('.caption-data-wrap',
                item.data.map(function (data, index) {
                    return ce('.form-group',
                        ce(`label.control-label[for=active-list-data-${item.id}-${index}]`, data.label),
                        ce(`textarea#active-list-data-${item.id}-${index}.form-control.code[rows=${data.linesNumber}][readonly]`, data.text.escapeHTML())
                    );
                })
            )
        );
    },

    render($super) {
        $super();

        const m = decodeURIComponent(location.href).match('/id/([^0-9]*[0-9]+)');
        if (!m) {
            return;
        }

        const el = $(`active-list-item-${m[1]}`);
        if (el) {
            $$('.msg-box').each(function (msg) {
                el.down('.caption-header-wrap').insert({ top: msg });
            });
            this.scrollToItem(el);
        }
    },

    onCaptionHeadWrapClick(item, event) {
        if (event && event.findElement('.caption-head-action, .caption-head-widget')) {
            return;
        }

        this.toggleItem(item);

        const el = this.getItemElement(item);
        if (el.hasClassName('active-list-item-collapsed')) {
            return;
        }

        const itemBottom = el.cumulativeOffset().top + el.getHeight();
        const viewportBottom = document.viewport.getScrollOffsets().top + document.viewport.getHeight();
        if (itemBottom + 40 > viewportBottom) {
            this.scrollToItem(el, 500);
        }
    },

    getItemElement(item) {
        return $(`active-list-item-${item.id}`);
    },

    _addEvents() {
        this._componentElement.select('dropdown-toggle').each(function (element) {
            element.observe('click', function (e) {
                Event.stop(e);
                const hadClassName = this.up('.btn-group').hasClassName('btn-group-open');
                $$('.active-list .dropdown-toggle').each(function (elem) {
                    elem.up('.btn-group').removeClassName('btn-group-open');
                });
                if (!hadClassName) {
                    this.up('.btn-group').addClassName('btn-group-open');
                }
            });
            $(document.body).observe('click', function () {
                element.up('.btn-group').removeClassName('btn-group-open');
            });
        });

        this._componentElement.select('.a-toolbar-buttons-misc .btn-search').invoke('observe', 'click', function () {
            this.up('.a-toolbar')
                .toggleClassName('a-toolbar-collapsed')
                .toggleClassName('a-toolbar-expanded');
        });

        this._componentElement.select('.a-toolbar-buttons-misc .btn-settings').each(function (el) {
            DropdownManager.register(el,
                function () {
                    return el.up().hasClassName('a-toolbar-buttons-settings-active');
                },
                function () {
                    el.up()
                        .addClassName('a-toolbar-buttons-settings-active')
                        .removeClassName('a-toolbar-buttons-settings-inactive');
                },
                function () {
                    el.up()
                        .addClassName('a-toolbar-buttons-settings-inactive')
                        .removeClassName('a-toolbar-buttons-settings-active');
                }
            );
        }, this);

        this._componentElement.select('.caption-services .close').each(function (element) {
            element.observe('click', function (e) {
                Event.stop(e);
                const closeUrl = this.readAttribute('data-close-url');
                this.up('.caption').select('.caption-services').map(Element.show);
                this.up('.caption-services').remove();
                new Ajax.Request(Jsw.baseUrl + closeUrl, { method: 'post' });
            });
        });

        this._initToolbar();
    },

    showInformer() {},

    _initToolbar() {
        this._componentElement.select('.a-toolbar-buttons-main').each(function (toolbarEl) {
            const itemEls = toolbarEl.childElements();

            const menuEl = new Element('span', { class: 'btn-group' })
                .insert(
                    '<span class="btn dropdown-toggle">' +
                        '<button type="button">' +
                            `<i class="icon"><img src="${Jsw.skinUrl}/icons/16/plesk/menu.png" alt=""></i>` +
                            ' <em class="caret"></em>' +
                        '</button>' +
                    '</span>' +
                    '<ul class="dropdown-menu pull-right"></ul>'
                );

            menuEl.observe('click', function (event) {
                event.stopPropagation();
                menuEl.toggleClassName('btn-group-open');

                const dropdownMenu = menuEl.down('.dropdown-menu');
                if (dropdownMenu.visible()) {
                    dropdownMenu.setStyle({
                        right: 0,
                    });
                    const offset = dropdownMenu.cumulativeOffset();
                    const { left } = toolbarEl.cumulativeOffset();
                    if (offset.left < left) {
                        dropdownMenu.setStyle({
                            right: `${offset.left - left}px`,
                        });
                    }
                }
            });
            $(document.body).observe('click', function () {
                menuEl.removeClassName('btn-group-open');
            });
            toolbarEl.insert(menuEl);

            const layout = menuEl.getLayout();
            toolbarEl._menuEl = {
                el: menuEl,
                width: menuEl.getWidth() + layout.get('margin-left') + layout.get('margin-right'),
            };
            menuEl.hide();

            toolbarEl._items = [];
            itemEls.each(function (itemEl) {
                const itemElClone = itemEl.clone(true);
                itemElClone.removeClassName('btn');

                const liEl = new Element('li').insert(itemElClone);
                menuEl.down('ul').insert(liEl);

                const layout = itemEl.getLayout();
                toolbarEl._items.push({
                    el: itemEl,
                    elInMenu: liEl,
                    width: itemEl.getWidth() + layout.get('margin-left') + layout.get('margin-right'),
                });
            });
        });

        const onWindowResize = () => {
            $$('.a-toolbar-buttons-main').each(function (toolbarEl) {
                let width = toolbarEl.getWidth();
                width -= toolbarEl._menuEl.width;

                toolbarEl._items.each(function (item) {
                    if (width > item.width) {
                        item.el.show();
                        item.elInMenu.hide();
                        width -= item.width;
                    } else {
                        item.el.hide();
                        item.elInMenu.show();
                        width = -1;
                    }
                });
                if (width === -1) {
                    toolbarEl._menuEl.el.show();
                } else {
                    toolbarEl._menuEl.el.hide();
                }
            });
        };

        onWindowResize();
        Event.observe(window, 'resize', onWindowResize);
    },

    toggleItem(item) {
        this.getItemElement(item)
            .toggleClassName('active-list-item-collapsed')
            .toggleClassName('active-list-item-expanded');
        this.stateCollapsed.set(item, !this.stateCollapsed.get(item));
    },

    scrollToItem(el, duration) {
        const startTime = new Date().getTime();
        const from = document.viewport.getScrollOffsets().top;
        let to = el.cumulativeOffset().top - $$('.page-main')[0].cumulativeOffset().top - 40;

        if ($('classic-mode-navigation')) {
            to -= $('classic-mode-navigation').getHeight();
        }

        const frame = () => {
            let progress = duration ? (new Date().getTime() - startTime) / duration : 1;
            if (progress > 1) {
                progress = 1;
            }

            window.scrollTo(0, Math.round(from + ((to - from) * progress)));

            if (progress < 1) {
                setTimeout(frame, 50);
            }
        };
        frame();
    },
});



// WEBPACK FOOTER //
// ./jsw/active-list.js